/**
 * Resolved pack-surface cache — the synchronous read side of the pack registry (ADR-066,
 * INC-118 / INC-C of `multi-pack-runtime`).
 *
 * Dynamic `import()` makes pack resolution async, but the host store/composables must resolve a
 * pack's surface SYNCHRONOUSLY at store-creation / composable-call time (SPEC §4.1/§4.2). This
 * module bridges the two: a per-pack module cache that {@link preloadPack} fills asynchronously
 * (INC-D calls it at picker-confirm / route entry) and {@link getResolvedUi} / {@link getResolvedData}
 * read synchronously, throwing if the pack was not preloaded.
 *
 * NO SYNCHRONOUS PACK IN THE BROWSER (ADR-099 D6): the browser build seeds no pack here, so the
 * hidden reference pack (dnd-4.5) stays out of the main bundle. Every app path calls
 * {@link preloadPack} before it reads a surface. Vitest seeds the reference pack in
 * `vitest.setup.ts`; node-side tooling (`tools/qa/seed*.mjs`) preloads its own pack.
 *
 * NOTE: a surface is bound per pack id, not per call — `preloadPack` is idempotent and the store
 * shell binds a character's pack at first creation (see `stores/character.ts`).
 */
import type { PackId, PackDataSurface, PackUiSurface } from './registry'
import { packRegistry } from './registry'
import { createEngine, type Engine } from '@/engine/createEngine'

const dataCache = new Map<PackId, PackDataSurface>()
const uiCache = new Map<PackId, PackUiSurface>()
const engineCache = new Map<PackId, Engine>()

/** Synchronously read a preloaded pack's DATA surface; throws if it was not preloaded. */
export function getResolvedData(packId: PackId): PackDataSurface {
  const surface = dataCache.get(packId)
  if (!surface) {
    throw new Error(
      `Pack data surface for '${packId}' is not resolved — preload it first (ADR-066).`,
    )
  }
  return surface
}

/** Synchronously read a preloaded pack's UI surface; throws if it was not preloaded. */
export function getResolvedUi(packId: PackId): PackUiSurface {
  const surface = uiCache.get(packId)
  if (!surface) {
    throw new Error(`Pack UI surface for '${packId}' is not resolved — preload it first (ADR-066).`)
  }
  return surface
}

/**
 * The engine for a preloaded pack, created once per id (`createEngine` validates the pack on every
 * call, so it is never created per component/composable). Lives here rather than in one consumer
 * because several read it — the choice picker (Layer C options) and the roll log (the declared
 * `play.resolution` ladder, INC-132). Throws if the pack was not preloaded.
 */
export function getEngine(packId: PackId): Engine {
  let engine = engineCache.get(packId)
  if (!engine) {
    engine = createEngine(getResolvedData(packId).activePack)
    engineCache.set(packId, engine)
  }
  return engine
}

/** Whether both surfaces of `packId` are resolved and ready for synchronous reads. */
export function isPackResolved(packId: PackId): boolean {
  return dataCache.has(packId) && uiCache.has(packId)
}

/**
 * Resolve (and cache) both surfaces of a registered pack. Idempotent. INC-D calls this at
 * picker-confirm and at `CampaignView` route entry so downstream consumers stay synchronous.
 */
export async function preloadPack(packId: PackId): Promise<void> {
  const entry = packRegistry.get(packId)
  if (!entry) {
    throw new Error(`Pack '${packId}' is not in the registry (filtered out by VITE_PACKS?).`)
  }
  if (!dataCache.has(packId)) dataCache.set(packId, await entry.loadData())
  if (!uiCache.has(packId)) uiCache.set(packId, await entry.loadUi())
}
